import React from 'react';
import PropTypes from 'prop-types';
import { usePrefs, useLabels } from 'wsm-common-data';
import formatCurrency from '../utils/formatCurrency';

const FinancingDetails = ({ offer }) => {
	const prefs = usePrefs();
	const labels = useLabels();
	const showShortTitle = prefs.showShortTitle === 'true';
	const { incentiveData, incentiveType } = offer;

	// AC #7: return null when showShortTitle active or no incentiveData
	if (showShortTitle || !incentiveData) return null;

	const hasAprTerms =
		(incentiveType === 'APR' || incentiveType === 'CASH_AND_APR') &&
		incentiveData.aprTerms?.length > 0;

	const hasCash =
		(incentiveType === 'CASH' || incentiveType === 'CASH_AND_APR') &&
		incentiveData.cashOption != null;

	const isLease =
		incentiveType === 'LEASE' && incentiveData.monthlyPayment != null;

	// If no meaningful data to display, return null
	if (!hasAprTerms && !hasCash && !isLease) return null;

	const cashBackLabel = labels.get('CASH_BACK');
	const throughLenderLabel = labels.get('INCENTIVES_THROUGH_LENDER');
	const dueAtSigningLabel = labels.get('CASH_DUE_AT_SIGNING');
	const aprForTermLabel = labels.get('INCENTIVES_APR_FOR_TERM');
	const perMonthLabel = labels.get('INCENTIVES_PER_MONTH');
	const monthLeaseLabel = labels.get('INCENTIVES_MONTH_LEASE');

	const renderCashItem = () => {
		const formatted = formatCurrency(incentiveData.cashOption);
		if (!formatted) return null;
		return (
			<li aria-label={`${cashBackLabel}: ${formatted}`}>
				{formatted} {cashBackLabel}
			</li>
		);
	};

	const renderAprItems = () => {
		const { bestAprTerm, lenderName } = incentiveData;

		const headline = bestAprTerm
			? aprForTermLabel
					.replace('{rate}', bestAprTerm.value)
					.replace('{term}', bestAprTerm.term)
			: null;

		return (
			<>
				{headline && <li>{headline}</li>}
				{lenderName && (
					<li aria-label={`${throughLenderLabel} ${lenderName}`}>
						{throughLenderLabel} {lenderName}
					</li>
				)}
			</>
		);
	};

	const renderLeaseItems = () => {
		const { monthlyPayment, cashDue, aprTerms, lenderName } = incentiveData;
		const leaseTerm = aprTerms?.[0]?.term;

		const monthlyDisplay = perMonthLabel.replace(
			'{amount}',
			formatCurrency(monthlyPayment)
		);

		const leaseTermDisplay = leaseTerm
			? monthLeaseLabel.replace('{term}', leaseTerm)
			: null;

		return (
			<>
				<li>{monthlyDisplay}</li>
				{leaseTermDisplay && <li>{leaseTermDisplay}</li>}
				{cashDue != null && (
					<li>
						{formatCurrency(cashDue)} {dueAtSigningLabel}
					</li>
				)}
				{lenderName && (
					<li aria-label={`${throughLenderLabel} ${lenderName}`}>
						{throughLenderLabel} {lenderName}
					</li>
				)}
			</>
		);
	};

	return (
		<>
			{hasCash && renderCashItem()}
			{hasAprTerms && renderAprItems()}
			{isLease && renderLeaseItems()}
		</>
	);
};

FinancingDetails.propTypes = {
	offer: PropTypes.shape({
		incentiveType: PropTypes.string.isRequired,
		incentiveData: PropTypes.shape({
			cashOption: PropTypes.number,
			lenderName: PropTypes.string,
			bestAprTerm: PropTypes.shape({
				term: PropTypes.number,
				value: PropTypes.number
			}),
			aprTerms: PropTypes.arrayOf(
				PropTypes.shape({
					term: PropTypes.number,
					value: PropTypes.number
				})
			),
			monthlyPayment: PropTypes.number,
			cashDue: PropTypes.number
		})
	}).isRequired
};

export default FinancingDetails;
